import { defaultDocumentLoader, issue, signPresentation, verifyCredential } from '@digitalbazaar/vc';
import { extendContextLoader } from 'jsonld-signatures';
import * as jose from 'jose';

import ed25519Context from 'ed25519-signature-2020-context';
import { Ed25519VerificationKey2020 } from
  '@digitalbazaar/ed25519-verification-key-2020';
import { Ed25519Signature2020 } from '@digitalbazaar/ed25519-signature-2020';

import * as Ed25519Multikey from '@digitalbazaar/ed25519-multikey';
import {DataIntegrityProof} from '@digitalbazaar/data-integrity';
import {cryptosuite as eddsaRdfc2022CryptoSuite} from
  '@digitalbazaar/eddsa-rdfc-2022-cryptosuite';
import dataIntegrityContext from '@digitalbazaar/data-integrity-context';

import internalContext from './contexts';

// setup static document loader
const documentLoader = extendContextLoader(async url => {
  const context = [internalContext, dataIntegrityContext, ed25519Context]
    .map(d => d.contexts.get(url))
    .find(c => c !== undefined);
  if (context !== undefined) {
    return {
      contextUrl: null,
      documentUrl: url,
      document: context
    };
  }
  console.log("could not find context " + url)
  return defaultDocumentLoader(url);
});

function addDocument(url, context) {
  internalContext.add(url, context);
}

const suites = new Map();
async function getSuite(controller) {
  if (suites.has(controller)) return suites.get(controller);

  const keyPair = await Ed25519Multikey.generate({ controller });
  // console.log("signing keys", JSON.stringify(keyPair, null, 2));
  const suite = new DataIntegrityProof({
    signer: keyPair.signer(), cryptosuite: eddsaRdfc2022CryptoSuite
  });

  // console.log("signing suite", JSON.stringify(suite, null, 2));
  suites.set(controller, suite);
  // add the verification method document for the generated key to the documentLoader cache.
  const verificationMethodDoc = await keyPair.export({publicKey: true});
  addDocument(suite.signer.id, verificationMethodDoc);

  // add a controller document for the generated eddsa multikey to the documentLoader cache.
  // The "id" must equal the vc.issuer.id to prove the issuer has control of the key.
  addDocument(controller, {
    "@context": [
      "https://www.w3.org/ns/did/v1",
      "https://w3id.org/security/multikey/v1"
    ],
    "id": controller,
    "assertionMethod": [
      suite.signer.id
    ]
  });
  return suite;
}

const jwks = new Map();
async function getJwk(controller) {
  if (jwks.has(controller)) return jwks.get(controller);
  const jwk = await jose.generateKeyPair('RS256');
  jwks.set(controller, jwk);
  return jwk;
}

async function loadContexts(exampleContexts) {
  const promises = new Array();
  for (const url of exampleContexts) {
    if (typeof url === "object") continue;
    let context = internalContext.contexts.get(url);
    if (context === undefined) {
      promises.push(new Promise(async (resolve, reject) => {
        try {
          const response = await fetch(url);
          if (response.ok) {
            context = await response.json();
            addDocument(url, context);
          }
        } catch (e) {
          console.error(`Cannot fetch ${url}`);
        } finally {
          resolve();
        }
      }));
    }
  }
  await Promise.all(promises);
}

/**
 * Verifies a verifiable credential using information in the proofs
 *   - Creates an array of suites from the proofs attached to the credential
 *   - Calls vc.verifyCredential to perform the actual verification
 *
 * @param {object} [options={}] - The options.
 *
 * @param {object} options.credential - Verifiable credential.
 * @param {Function} [options.documentLoader] - A document loader.
 *
 * @returns {Promise<VerifyCredentialResult>} The verification result.
 */
async function verifyCredentialUsingProofs(options = {}) {
  const { credential } = options;
  try {
    if (!credential) {
      throw new TypeError(
        'A "credential" property is required for verifying.');
    }
    if (!credential.proof) {
      throw new TypeError(
        'A "proof" property is required for verifying.');
    }
    const suites = new Map();
    for (const proof of credential.proof) {
      if (!suites.has(proof.verificationMethod)) {
        let controller;
        let publicKeyMultibase;
        if (proof.verificationMethod.includes("#")) {
          controller = proof.verificationMethod.split("#")[0];
          publicKeyMultibase = proof.verificationMethod.split("#")[1];
        } else {
          controller = proof.verificationMethod;
          publicKeyMultibase = proof.verificationMethod.split("did:key:")[1];
        }
        let suite;
        const keypair = new Ed25519VerificationKey2020({
          id: proof.verificationMethod,
          controller,
          publicKeyMultibase
        });
      if (proof.type === "Ed25519Signature2020") {
          // console.log("reconstructed keypair", JSON.stringify(keypair, null, 2));
          suite = new Ed25519Signature2020({ key: keypair });
        } else if  (proof.type === "DataIntegrityProof") {
          // const ed25519Multikey = await Ed25519Multikey.from(keypair);
          suite = new DataIntegrityProof({
            cryptosuite: eddsaRdfc2022CryptoSuite
          });
        } else {
          throw new TypeError(
            'A valid "proof.type" property is required for verifying.');
        }
        // console.log("reconstructed suite", JSON.stringify(suite, null, 2));
        suites.set(proof.verificationMethod, suite);
      }
    }
    options.suite = Array.from(suites.values());
    // don't check the status of the credential
    options.checkStatus = () => { return {verified: true}; };
    return await verifyCredential(options);
  } catch (error) {
    return {
      verified: false,
      results: [{ credential, verified: false, error }],
      error
    };
  }
}

// convert an XML Schema v1.` Datetime value to a UNIX timestamp
function xmlDateTimeToUnixTimestamp(xmlDateTime) {
  if (!xmlDateTime) {
    return undefined;
  }

  return Date.parse(xmlDateTime) / 1000;
}

// transform the input credential to a signed JWT
async function transformCredentialToJwt({ credential, jwk }) {
  const publicKey = await jose.exportJWK(jwk.publicKey);
  const header = { alg: 'RS256', typ: 'JWT', jwk: publicKey };

  let payload;
  // vcdm  1.1
  if (credential['@context'].includes("https://www.w3.org/2018/credentials/v1")) {
    payload = {
      vc: credential
    };
  } else if (credential['@context'].includes("https://www.w3.org/ns/credentials/v2")){
    // vcdm 2.0
    payload = credential;
  }

  if (credential.expirationDate) {
    payload.exp = xmlDateTimeToUnixTimestamp(credential.expirationDate);
  }
  if (credential.issuer) {
    if (typeof credential.issuer === "string") {
      payload.iss = credential.issuer;
    } else if (credential.issuer.id) {
      payload.iss = credential.issuer.id;
    }
  }
  if (credential.issuanceDate) {
    payload.nbf = xmlDateTimeToUnixTimestamp(credential.issuanceDate);
  }
  if (credential.id) {
    payload.jti = credential.id;
  }
  if (credential.credentialSubject.id) {
    payload.sub = credential.credentialSubject.id;
  }

  const jwt = await new jose.SignJWT(payload)
    .setProtectedHeader(header)
    .sign(jwk.privateKey);

  return { header, payload, jwt };
};

// transform the input credential to a JWT description
async function transformCredentialToJwtDescription({ credential, jwk }) {
  const { header, payload, jwt } = await transformCredentialToJwt({ credential, jwk });

  // create the JWT description
  let description = '---------------- JWT header ---------------\n' +
    JSON.stringify(header, null, 2);

  description += '\n\n--------------- JWT payload ---------------\n' +
    '// NOTE: The example below uses a valid VC-JWT serialization\n' +
    '//       that duplicates the iss, nbf, jti, and sub fields in the\n' +
    '//       Verifiable Credential (vc) field.\n\n' +
    JSON.stringify(payload, null, 2);

  return description + '\n\n--------------- JWT ---------------\n\n' + jwt;
};

// transform the input presentation to a JWT
async function transformPresentationToJwt({ presentation, jwk }) {
  const publicKey = await jose.exportJWK(jwk.publicKey);
  const header = { alg: 'RS256', typ: 'JWT', jwk: publicKey };
  const payload = {
    vp: presentation
  };
  if (presentation.holder) {
    if (typeof presentation.holder === "string") {
      payload.iss = presentation.holder;
    } else if (presentation.holder.id) {
      payload.iss = presentation.holderid;
    }
  }
  if (presentation.id) {
    payload.jti = presentation.id;
  }

  const jwt = await new jose.SignJWT(payload)
    .setProtectedHeader(header)
    .sign(jwk.privateKey);

  return { header, payload, jwt };
};

// transform the input presentation to a JWT description
async function transformPresentationToJwtDescription({ presentation, jwk }) {
  const { header, payload, jwt } = await transformPresentationToJwt({ presentation, jwk });

  // create the JWT description
  let description = '---------------- JWT header ---------------\n' +
    JSON.stringify(header, null, 2);

  description += '\n\n--------------- JWT payload ---------------\n' +
    '// NOTE: The example below uses a valid VP-JWT serialization\n' +
    '//       that duplicates the iss and jti fields in the\n' +
    '//       Verifiable Presentation (vp) field.\n\n' +
    JSON.stringify(payload, null, 2);

  return description + '\n\n--------------- JWT ---------------\n\n' + jwt;
};

async function attachCredentialProof({ credential, suite }) {
  const credentialCopy = JSON.parse(JSON.stringify(credential));
  if (!("proof" in credentialCopy)) {
    credentialCopy.proof = new Array();
  }
  return issue({ credential: credentialCopy, suite, documentLoader });
};

async function attachPresentationProof({ presentation, suite }) {
  const presentationCopy = JSON.parse(JSON.stringify(presentation));
  if (!("proof" in presentationCopy)) {
    presentationCopy.proof = new Array();
  }
  return signPresentation({ presentation: presentationCopy, challenge: "challenge", suite, documentLoader });
};

function addVcExampleStyles() {
  const exampleStyles = document.createElement('style');

  exampleStyles.innerHTML += `
  .vc-tabbed, .vp-tabbed {
    overflow-x: hidden;
    margin: 0 0;
  }

  .vc-tabbed [type="radio"], .vp-tabbed [type="radio"] {
    display: none;
  }

  .vc-tabs, .vp-tabs {
    display: flex;
    align-items: stretch;
    list-style: none;
    padding: 0;
    border-bottom: 1px solid #ccc;
  }

  li.vc-tab, li.vp-tab {
    margin: unset;
  }

  .vc-tab > label, .vp-tab > label {
    display: block;
    margin-bottom: -1px;
    padding: .4em .5em;
    border: 1px solid #ccc;
    border-top-right-radius: .4em;
    border-top-left-radius: .4em;
    background: #eee;
    color: #666;
    cursor: pointer;
    transition: all 0.3s;
  }
  .vc-tab:hover label, .vp-tab:hover {
    border-left-color: #333;
    border-top-color: #333;
    border-right-color: #333;
    color: #333;
  }

  .vc-tab-content, .vp-tab-content {
    display: none;
  }

  .vc-tabbed [type="radio"]:nth-of-type(1):checked ~ .vc-tabs .vc-tab:nth-of-type(1) label,
  .vc-tabbed [type="radio"]:nth-of-type(2):checked ~ .vc-tabs .vc-tab:nth-of-type(2) label,
  .vc-tabbed [type="radio"]:nth-of-type(3):checked ~ .vc-tabs .vc-tab:nth-of-type(3) label {
    border-bottom-color: #fff;
    background: #fff;
    color: #222;
  }

  .vc-tabbed [type="radio"]:nth-of-type(1):checked ~ .vc-tab-content:nth-of-type(1),
  .vc-tabbed [type="radio"]:nth-of-type(2):checked ~ .vc-tab-content:nth-of-type(2),
  .vc-tabbed [type="radio"]:nth-of-type(3):checked ~ .vc-tab-content:nth-of-type(3) {
    display: block;
  }

  .vp-tabbed [type="radio"]:nth-of-type(1):checked ~ .vp-tabs .vp-tab:nth-of-type(1) label,
  .vp-tabbed [type="radio"]:nth-of-type(2):checked ~ .vp-tabs .vp-tab:nth-of-type(2) label,
  .vp-tabbed [type="radio"]:nth-of-type(3):checked ~ .vp-tabs .vp-tab:nth-of-type(3) label {
    border-bottom-color: #fff;
    background: #fff;
    color: #222;
  }

  .vp-tabbed [type="radio"]:nth-of-type(1):checked ~ .vp-tab-content:nth-of-type(1),
  .vp-tabbed [type="radio"]:nth-of-type(2):checked ~ .vp-tab-content:nth-of-type(2),
  .vp-tabbed [type="radio"]:nth-of-type(3):checked ~ .vp-tab-content:nth-of-type(3) {
    display: block;
  }`;

  document.getElementsByTagName('head')[0].appendChild(exampleStyles);
}

function createCredentialTabs(example, proofExampleIndex, verifiableCredentialProof, verifiableCredentialJwt) {
  // set up the tabbed content
  const tabbedContent = document.createElement('div');
  tabbedContent.setAttribute('class', 'vc-tabbed');

  // set up the unsigned button
  const unsignedTabBtn = document.createElement('input');
  unsignedTabBtn.setAttribute('type', 'radio');
  unsignedTabBtn.setAttribute('id', `vc-tab${proofExampleIndex}1`);
  unsignedTabBtn.setAttribute('name', `vc-tabs${proofExampleIndex}`);
  unsignedTabBtn.setAttribute('checked', 'checked');
  tabbedContent.appendChild(unsignedTabBtn);

  // set up the signed proof button
  const signedProofTabBtn = document.createElement('input');
  signedProofTabBtn.setAttribute('type', 'radio');
  signedProofTabBtn.setAttribute('id', `vc-tab${proofExampleIndex}2`);
  signedProofTabBtn.setAttribute('name', `vc-tabs${proofExampleIndex}`);
  tabbedContent.appendChild(signedProofTabBtn);

  // set up the signed JWT button
  const signedJwtTabBtn = document.createElement('input');
  signedJwtTabBtn.setAttribute('type', 'radio');
  signedJwtTabBtn.setAttribute('id', `vc-tab${proofExampleIndex}3`);
  signedJwtTabBtn.setAttribute('name', `vc-tabs${proofExampleIndex}`);
  tabbedContent.appendChild(signedJwtTabBtn);

  // set up the tab labels
  const tabLabels = document.createElement("ul");
  tabLabels.setAttribute('class', 'vc-tabs');
  tabbedContent.appendChild(tabLabels);

  const unsignedLabel = document.createElement("li");
  unsignedLabel.setAttribute('class', 'vc-tab');
  unsignedLabel.innerHTML = `<label for='${unsignedTabBtn.getAttribute('id')}'>Credential</label>`;
  tabLabels.appendChild(unsignedLabel)

  const signedProofLabel = document.createElement("li");
  signedProofLabel.setAttribute('class', 'vc-tab');
  signedProofLabel.innerHTML = `<label for='${signedProofTabBtn.getAttribute('id')}'>Verifiable Credential (with proof)</label>`;
  tabLabels.appendChild(signedProofLabel)

  const signedJwtLabel = document.createElement("li");
  signedJwtLabel.setAttribute('class', 'vc-tab');
  signedJwtLabel.innerHTML = `<label for='${signedJwtTabBtn.getAttribute('id')}'>Verifiable Credential (as JWT)</label>`;
  tabLabels.appendChild(signedJwtLabel)

  // append the tabbed content
  const container = example.parentNode;
  const unsignedContent = document.createElement('div');
  unsignedContent.setAttribute('class', 'vc-tab-content');
  // Move the credential example to the unsigned tab
  unsignedContent.append(example);
  tabbedContent.appendChild(unsignedContent);

  const signedProofContent = document.createElement('div');
  signedProofContent.setAttribute('class', 'vc-tab-content');
  // signedProofContent.innerHTML = `<pre>${JSON.stringify(verifiableCredentialProof, null, 2).match(/.{1,80}/g).join('\n')}</pre>`;
  signedProofContent.innerHTML = `<pre>${JSON.stringify(verifiableCredentialProof, null, 2)}</pre>`;
  tabbedContent.appendChild(signedProofContent);

  const signedJwtContent = document.createElement('div');
  signedJwtContent.setAttribute('class', 'vc-tab-content');
  signedJwtContent.innerHTML = `<pre>${verifiableCredentialJwt.match(/.{1,80}/g).join('\n')}</pre>`;
  tabbedContent.appendChild(signedJwtContent);

  // replace the original example with the tabbed content

  container.append(tabbedContent);
}

function createPresentationTabs(example, proofExampleIndex, verifiablePresentationProof, verifiablePresentationJwt) {

  // set up the tabbed content
  const tabbedContent = document.createElement('div');
  tabbedContent.setAttribute('class', 'vp-tabbed');

  // set up the unsigned button
  const unsignedTabBtn = document.createElement('input');
  unsignedTabBtn.setAttribute('type', 'radio');
  unsignedTabBtn.setAttribute('id', `vp-tab${proofExampleIndex}1`);
  unsignedTabBtn.setAttribute('name', `vp-tabs${proofExampleIndex}`);
  unsignedTabBtn.setAttribute('checked', 'checked');
  tabbedContent.appendChild(unsignedTabBtn);

  // set up the signed proof button
  const signedProofTabBtn = document.createElement('input');
  signedProofTabBtn.setAttribute('type', 'radio');
  signedProofTabBtn.setAttribute('id', `vp-tab${proofExampleIndex}2`);
  signedProofTabBtn.setAttribute('name', `vp-tabs${proofExampleIndex}`);
  tabbedContent.appendChild(signedProofTabBtn);

  // set up the signed JWT button
  const signedJwtTabBtn = document.createElement('input');
  signedJwtTabBtn.setAttribute('type', 'radio');
  signedJwtTabBtn.setAttribute('id', `vp-tab${proofExampleIndex}3`);
  signedJwtTabBtn.setAttribute('name', `vp-tabs${proofExampleIndex}`);
  tabbedContent.appendChild(signedJwtTabBtn);

  // set up the tab labels
  const tabLabels = document.createElement("ul");
  tabLabels.setAttribute('class', 'vp-tabs');
  tabbedContent.appendChild(tabLabels);

  const unsignedLabel = document.createElement("li");
  unsignedLabel.setAttribute('class', 'vp-tab');
  unsignedLabel.innerHTML = `<label for='${unsignedTabBtn.getAttribute('id')}'>Presentation</label>`;
  tabLabels.appendChild(unsignedLabel)

  const signedProofLabel = document.createElement("li");
  signedProofLabel.setAttribute('class', 'vp-tab');
  signedProofLabel.innerHTML = `<label for='${signedProofTabBtn.getAttribute('id')}'>Verifiable Presentation (with proof)</label>`;
  tabLabels.appendChild(signedProofLabel)

  const signedJwtLabel = document.createElement("li");
  signedJwtLabel.setAttribute('class', 'vp-tab');
  signedJwtLabel.innerHTML = `<label for='${signedJwtTabBtn.getAttribute('id')}'>Verifiable Presentation (as JWT)</label>`;
  tabLabels.appendChild(signedJwtLabel)

  // append the tabbed content

  const container = example.parentNode;
  const unsignedContent = document.createElement('div');
  unsignedContent.setAttribute('class', 'vp-tab-content');
  // Move the presentation example to the unsigned tab
  unsignedContent.append(example);
  tabbedContent.appendChild(unsignedContent);

  const signedProofContent = document.createElement('div');
  signedProofContent.setAttribute('class', 'vp-tab-content');
  // signedProofContent.innerHTML = `<pre>${JSON.stringify(verifiablePresentationProof, null, 2).match(/.{1,80}/g).join('\n')}</pre>`;
  signedProofContent.innerHTML = `<pre>${JSON.stringify(verifiablePresentationProof, null, 2)}</pre>`;
  tabbedContent.appendChild(signedProofContent);

  const signedJwtContent = document.createElement('div');
  signedJwtContent.setAttribute('class', 'vp-tab-content');
  signedJwtContent.innerHTML = `<pre>${verifiablePresentationJwt.match(/.{1,80}/g).join('\n')}</pre>`;
  tabbedContent.appendChild(signedJwtContent);

  // replace the original example with the tabbed content

  container.append(tabbedContent);
}

async function createCredentialExamples() {

  // process every example that needs a vc-proof
  const vcProofExamples = document.querySelectorAll(".vc");
  let proofExampleIndex = 0;
  for (const example of vcProofExamples) {
    proofExampleIndex++;

    // extract and sign the example
    let credential = {};
    try {
      let exampleText = example.innerText;
      exampleText = exampleText.replace(/\/\/ .*$/gm, '');
      credential = JSON.parse(exampleText);
    } catch (e) {
      console.error('respec-vc error: Failed to create Verifiable Credential.',
        e, example.innerText);
      continue;
    }

    const nestedCredentialProofs = new Array();
    const nestedCredentialJwts = new Array();

    const verificationMethod = example.getAttribute('data-vc-vm');
    if (verificationMethod) getSuite(verificationMethod)

    if (Array.from(credential.type).includes("ClrCredential")) {
      // process each nested credential
      if (credential.credentialSubject.verifiableCredential === undefined) {
        console.warn("respec-vc warning: ClrCredential does not contain nested credentials.")
      } else {
        for (const nestedCredential of credential.credentialSubject.verifiableCredential) {

          const issuerId = (typeof (nestedCredential.issuer) === "object") ? nestedCredential.issuer.id : nestedCredential.issuer;

          // pre-load the contexts
          const nestedContexts = nestedCredential["@context"];
          if (nestedContexts) {
            await loadContexts(nestedContexts);
          }

          // attach the proof
          let verifiableCredentialProof;
          let suite;
          let jwk;

          if (verificationMethod) {
            // Use the verificationMethod that was passed in
            suite = await getSuite(verificationMethod);
            jwk = await getJwk(verificationMethod);
          } else {
            // Create a keyPair and suite specific to the credential.issuer.
            suite = await getSuite(issuerId);
            jwk = await getJwk(issuerId);
          }

          try {
            verifiableCredentialProof = await attachCredentialProof({ credential: nestedCredential, suite });
          } catch (e) {
            console.error(
              'respec-vc error: Failed to attach proof to Verifiable Credential.',
              e, example.innerText);
            if (e.details && e.details.event) {
              console.error(
                e.details.event.message,
                e.details.event.details
              );
            }
            continue;
          }

          try {
            // Verify the credential using the proof
            if (verifiableCredentialProof) {
              const results = await verifyCredentialUsingProofs({ credential: verifiableCredentialProof, documentLoader });
              console.log(verifiableCredentialProof.type.toString(), "Verified: " + results.verified, results.results);
            }
          } catch (e) {
            if (e.details && e.details.event) {
              console.error(
                "Failed to verify credential: " + e.details.event.message,
                e.details.event.details
              );
            } else {
              console.error("Failed to verify credential:", e, example.innerText);
            }
          }

          // convert to JWT
          let verifiableCredentialJwt;
          try {
            const { header, payload, jwt } = await transformCredentialToJwt({ credential: nestedCredential, jwk });
            verifiableCredentialJwt = jwt;
          } catch (e) {
            console.error(
              'respec-vc error: Failed to convert nested credential to JWT.',
              e, JSON.stringify(credential, 2));
            continue;
          }

          nestedCredentialProofs.push(verifiableCredentialProof);
          nestedCredentialJwts.push(verifiableCredentialJwt);
        }
      }
    }

    const issuerId = (typeof (credential.issuer) === "object") ? credential.issuer.id : credential.issuer;

    // pre-load the contexts
    await loadContexts(credential["@context"]);

    // attach the proof
    let verifiableCredentialProof;
    let suite;
    let jwk;
    if (verificationMethod) {
      // Use the verificationMethod that was passed in
      suite = await getSuite(verificationMethod);
      jwk = await getJwk(verificationMethod);
    } else {
      // Create a keyPair and suite specific to the credential.issuer.
      suite = await getSuite(issuerId);
      jwk = await getJwk(issuerId);
    }

    try {
      if (Array.from(credential.type).includes("ClrCredential") && nestedCredentialProofs.length > 0) {
        credential.credentialSubject.verifiableCredential = nestedCredentialProofs;
      }
      verifiableCredentialProof = await attachCredentialProof({ credential, suite });
    } catch (e) {
      console.error(
        'respec-vc error: Failed to attach proof to Verifiable Credential.',
        e, example.innerText);
      if (e.details && e.details.event) {
        console.error(
          e.details.event.message,
          e.details.event.details
        );
      }
    }

    try {
      // Verify the credential using the proof
      if (verifiableCredentialProof) {
        const results = await verifyCredentialUsingProofs({ credential: verifiableCredentialProof, documentLoader });
        console.log(verifiableCredentialProof.type.toString(), "Verified: " + results.verified, results.results);
      }
    } catch (e) {
      if (e.details && e.details.event) {
        console.error(
          "Failed to verify credential: " + e.details.event.message,
          e.details.event.details
        );
      } else {
        console.error("Failed to verify credential:", e, example.innerText);
      }
    }

    // convert to a JWT
    let verifiableCredentialJwt;
    try {
      if (Array.from(credential.type).includes("ClrCredential") && nestedCredentialJwts.length > 0) {
        credential.credentialSubject.verifiableCredential = nestedCredentialJwts;
      }
      verifiableCredentialJwt = await transformCredentialToJwtDescription({
        credential, jwk
      });
    } catch (e) {
      console.error(
        'respec-vc error: Failed to convert Credential to JWT.',
        e, example.innerText);
      continue;
    }

    createCredentialTabs(example, proofExampleIndex, verifiableCredentialProof, verifiableCredentialJwt);
  }
}

async function createPresentationExamples(suite, jwk) {
  // process every example that needs a vp-proof
  const vpProofExamples = document.querySelectorAll(".vp");
  let proofExampleIndex = 0;
  for (const example of vpProofExamples) {
    proofExampleIndex++;
    const verificationMethod = example.getAttribute("data-vp-vm");
    suite.verificationMethod =
      verificationMethod || "did:key:" + suite.signer.publicKeyMultibase;

    // extract and sign the example
    const originalText = example.innerHTML;
    let presentation = {};
    try {
      let exampleText = example.innerText;
      exampleText = exampleText.replace(/\/\/ .*$/gm, '');
      presentation = JSON.parse(exampleText);
    } catch (e) {
      console.error(
        'respec-vc error: Failed to create Presentation.',
        e, example.innerText);
      continue;
    }

    const credentialJwts = new Array();
    const credentialProofs = new Array();

    // process each nested credential
    for (const credential of presentation.verifiableCredential) {

      const nestedCredentialJwts = new Array();
      const nestedCredentialProofs = new Array();

      if (Array.from(credential.type).includes("ClrCredential")) {

        if (credential.credentialSubject.verifiableCredential === undefined) {
          console.warn("respec-vc warning: ClrCredential does not contain any nested credentials.");
          continue;
        }

        // process each nested credential
        for (const nestedCredential of credential.credentialSubject.verifiableCredential) {

          // pre-load the contexts
          await loadContexts(nestedCredential["@context"]);

          // attach the proof
          let verifiableCredentialProof;
          try {
            verifiableCredentialProof = await attachCredentialProof({ credential: nestedCredential, suite });
          } catch (e) {
            console.error(
              'respec-vc error: Failed to attach proof to nested credential.',
              e, JSON.stringify(credential, 2));
            continue;
          }

          // convert to JWT
          let verifiableCredentialJwt;
          try {
            const { header, payload, jwt } = await transformCredentialToJwt({ credential: nestedCredential, jwk });
            verifiableCredentialJwt = jwt;
          } catch (e) {
            console.error(
              'respec-vc error: Failed to convert nested credential to JWT.',
              e, JSON.stringify(credential, 2));
            continue;
          }

          nestedCredentialProofs.push(verifiableCredentialProof);
          nestedCredentialJwts.push(verifiableCredentialJwt);
        }
      }

      // pre-load the contexts
      await loadContexts(credential["@context"]);

      // attach the proof
      let verifiableCredentialProof;
      try {
        if (Array.from(credential.type).includes("ClrCredential")) {
          credential.credentialSubject.verifiableCredential = nestedCredentialProofs;
        }
        verifiableCredentialProof = await attachCredentialProof({ credential, suite });
      } catch (e) {
        console.error(
          'respec-vc error: Failed to attach proof to Credential.',
          e, JSON.stringify(credential, 2));
        continue;
      }

      // convert to JWT
      let verifiableCredentialJwt;
      try {
        if (Array.from(credential.type).includes("ClrCredential")) {
          credential.credentialSubject.verifiableCredential = nestedCredentialJwts;
        }
        const { header, payload, jwt } = await transformCredentialToJwt({ credential, jwk });
        verifiableCredentialJwt = jwt;
      } catch (e) {
        console.error(
          'respec-vc error: Failed to convert Credential to JWT.',
          e, JSON.stringify(credential, 2));
        continue;
      }

      credentialProofs.push(verifiableCredentialProof);
      credentialJwts.push(verifiableCredentialJwt);
    }

    // pre-load the contexts
    await loadContexts(presentation["@context"]);

    // attach the proof
    let verifiablePresentationProof;
    try {
      presentation.verifiableCredential = credentialProofs;
      verifiablePresentationProof = await attachPresentationProof({ presentation, suite });
    } catch (e) {
      console.error(
        'respec-vc error: Failed to attach proof to Presentation.',
        e, example.innerText);
      continue;
    }

    // convert to a JWT
    let verifiablePresentationJwt;
    try {
      presentation.verifiableCredential = credentialJwts;
      verifiablePresentationJwt = await transformPresentationToJwtDescription({
        presentation, jwk
      });
    } catch (e) {
      console.error(
        'respec-vc error: Failed to convert Presentation to JWT.',
        e, example.innerText);
      continue;
    }

    createPresentationTabs(example, proofExampleIndex, verifiablePresentationProof, verifiablePresentationJwt);
  }
}

async function createVcExamples() {
  // add styles for examples
  addVcExampleStyles();

  // generate base keypair and signature suite
  const keyPair = await Ed25519Multikey.generate();
  const suite = new DataIntegrityProof({
    signer: keyPair.signer(), cryptosuite: eddsaRdfc2022CryptoSuite
  });
  const jwk = await jose.generateKeyPair('RS256');

  await createCredentialExamples();
  await createPresentationExamples(suite, jwk);
}

// setup exports on window
window.respecVc = {
  addContext: addDocument,
  createVcExamples
}
